<template>
  <div>
    <ul
      v-ar-shadow-scroll
      class="mt-4 space-y-2 max-h-[320px] text-sm"
    >
      <li
        v-for="click in processedClicks"
        :key="click.shortUrlOid"
        :class="rowClasses"
      >
        <div :class="iconContainerClasses">
          <AM3Icon type="cursor-arrow-rays" />
        </div>
        <span>
          <!-- short url in tooltip, if we want to add later -->
          <!-- v-tooltip.bottom="`Short URL: ${click.shortUrl}`" -->
          Clicked on link - {{ click.targetUrl }}
          <span v-if="click.clickCount > 1">({{ click.clickCount }} clicks)</span>
        </span>
        <div class="mr-0 ml-auto text-secondary text-xs">
          <!-- {{ click.firstClickTs }} | {{ click.firstClickDate }} -->
          <AM3Formatter
            v-tooltip.bottom="formatDateTime(click.firstClickDate, { dateStyle: 'medium', timeStyle: 'short' })"
            type="relative-time"
            :value="click.firstClickDate.toISOString()"
          />
        </div>
      </li>
      <li
        v-if="!isBounced && !isFailed"
        :class="rowClasses"
      >
        <div :class="iconContainerClasses">
          <AM3Icon type="cursor-arrow-rays" />
        </div>
        <span v-if="hasClicked">
          Last Clicked
        </span>
        <span
          v-else
          class="text-secondary"
        >
          Has not clicked message
        </span>
        <div
          v-if="hasClicked"
          class="mr-0 ml-auto text-secondary text-xs"
        >
          <AM3Formatter
            v-tooltip.bottom="formatDateTime(props.lastClickTime as string, { dateStyle: 'medium', timeStyle: 'short' })"
            type="relative-time"
            :value="props.lastClickTime"
          />
        </div>
      </li>
      <li
        v-if="isEmail && !isBounced && !isFailed"
        :class="rowClasses"
      >
        <div :class="iconContainerClasses">
          <AM3Icon type="envelope-open" />
        </div>
        <span v-if="hasOpened">
          Last Open
        </span>
        <span
          v-else
          class="text-secondary"
        >
          Has not opened message
        </span>
        <div
          v-if="hasOpened"
          class="mr-0 ml-auto text-secondary text-xs"
        >
          <AM3Formatter
            v-tooltip.bottom="formatDateTime(props.lastOpenTime as string, { dateStyle: 'medium', timeStyle: 'short' })"
            type="relative-time"
            :value="props.lastOpenTime"
          />
        </div>
      </li>
      <li
        v-if="isBounced || isFailed"
        :class="rowClasses"
      >
        <div :class="[iconContainerClasses, isFailed ? 'bg-surfaces-danger' : 'bg-surfaces-warning']">
          <AM3Icon
            :type="isFailed ? 'x-circle' : 'exclamation-triangle'"
            :color="isFailed ? 'danger' : 'warning'"
          />
        </div>
        <span :class="isFailed ? 'text-danger' : 'text-warning'">
          {{ isFailed ? `${providerLabel} failed to send` : `${providerLabel} Bounced` }}
        </span>
      </li>
      <li :class="rowClasses">
        <div :class="iconContainerClasses">
          <AM3Icon type="envelope" />
        </div>
        <span>
          Sent
        </span>
        <div class="mr-0 ml-auto text-secondary text-xs">
          <AM3Formatter
            v-if="props.sentTime"
            v-tooltip.bottom="formatDateTime(props.sentTime, { dateStyle: 'medium', timeStyle: 'short' })"
            type="relative-time"
            :value="props.sentTime"
          />
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import AM3Formatter from '@/components/am3-generic/data/AM3Formatter.vue'
import { formatDateTime } from '@/utils/formatters'
import type { ShortUrlClick } from '@/store/modules/customer/types.ts'

// icon container
const iconContainerClasses: string = 'mr-2 group-hover:shadow-sm size-8 flex flex-col items-center justify-center rounded-full bg-surface border-surface-light border-4 border-solid'

// create a pseudo to draw the lines
const afterRowClasses: string = 'after:w-[2px] after:h-2 after:absolute after:left-[.90rem] after:-bottom-2 last:after:hidden'

// classes for the general shape of the click actions
const rowClasses: string = 'flex relative group flex-row items-center justify-start after:bg-surface-dark' + ' ' + afterRowClasses

const props = defineProps<{
  shortUrlClicks?: ShortUrlClick[]
  sentTime?: string
  lastOpenTime?: string
  lastClickTime?: string
  status?: string
  provider?: string
}>()

// Convert Unix timestamps to Date objects for proper formatting
const processedClicks = computed(() => {
  if (!props.shortUrlClicks) return []

  return props.shortUrlClicks.map(click => ({
    ...click,
    firstClickDate: new Date(click.firstClickTs * 1000),
    lastClickDate: new Date(click.lastClickTs * 1000),
  }))
})

const isEmail = computed(() => props.provider?.toLowerCase() === 'email')
const isBounced = computed(() => props.status === 'bounced')
const isFailed = computed(() => props.status === 'failed')
const hasOpened = computed(() => Boolean(props.lastOpenTime))
const hasClicked = computed(() => Boolean(props.lastClickTime))
const providerLabel = computed(() => (props.provider?.toLowerCase() === 'sms' ? 'SMS' : 'Email'))
</script>
